import { useState } from 'react';

import { ApiError } from '../../api/client';
import type { ManagerStatusDto } from '../../api/manager-statuses';
import { useCreateManagerStatus, useManagerStatuses, usePatchManagerStatus } from '../../api/manager-statuses';
import { Badge, Button, EmptyState, ErrorState, Input, Spinner } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import {
  STATUS_INACTIVE_LABEL,
  STATUS_NO_DELETE_HINT,
  isDuplicateLabel,
  normalizeStatusLabel,
  parseStatuses,
  sortStatuses,
  statusLabelChanged,
  statusLabelError,
  statusToggleLabel,
} from './statuses-model';

const DUPLICATE_HINT = 'Такой статус уже есть.';

/**
 * «Статусы менеджера» — the dictionary of contract #28/#28.1/#28.2 (ADDITIVE
 * v1.8.0, wave 9, T.Y9, Р9/D17), the vocabulary the «Статус менеджера» column of
 * the register and its filter read.
 *
 * Three writes and one deliberate absence:
 *   * #28.1 adds an entry (409 on a label another row already holds);
 *   * #28.2 renames it (the same 409) and deactivates/reactivates it;
 *   * there is NO delete — the journal rows reference the dictionary and must
 *     keep resolving, so deactivation is the off-switch, and the note under the
 *     table says exactly that in the words SR §5.3 pins.
 *
 * Deactivated rows stay in the list, greyed, with their badge — the operator has
 * to see what «только деактивировать» means on a row that is already off. They
 * are hidden until «Показать деактивированные» asks for them, which is a second
 * READ (`?includeInactive=`), not a client-side filter: the api owns the flag.
 */
export function StatusesTab() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const statuses = useManagerStatuses(includeInactive);
  const create = useCreateManagerStatus();
  const patch = usePatchManagerStatus();

  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  const [toolbarError, setToolbarError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [rowErrors, setRowErrors] = useState<Record<number, string | undefined>>({});

  const rows = sortStatuses(parseStatuses(statuses.data));

  const setRowError = (statusId: number, message: string | undefined) =>
    setRowErrors((current) => ({ ...current, [statusId]: message }));

  const submitCreate = () => {
    const error = statusLabelError(draft);
    setDraftError(error ?? null);
    setToolbarError(null);
    if (error !== undefined) return;
    if (isDuplicateLabel(draft, rows)) {
      setToolbarError(DUPLICATE_HINT);
      return;
    }
    create.mutate(
      { label: normalizeStatusLabel(draft) as string },
      {
        onSuccess: () => setDraft(''),
        onError: (cause) => setToolbarError(describeStatusError(cause, 'Не удалось добавить статус.')),
      },
    );
  };

  const submitRename = (row: ManagerStatusDto) => {
    setRowError(row.id, undefined);
    if (!statusLabelChanged(row, editDraft)) {
      setEditingId(null);
      return;
    }
    // The courtesy check the api would answer with 409 anyway — said here, where
    // the row that holds the name is on screen.
    if (isDuplicateLabel(editDraft, rows, row.id)) {
      setRowError(row.id, DUPLICATE_HINT);
      return;
    }
    patch.mutate(
      { statusId: row.id, patch: { label: normalizeStatusLabel(editDraft) as string } },
      {
        onSuccess: () => setEditingId(null),
        onError: (cause) => setRowError(row.id, describeStatusError(cause, 'Не удалось переименовать статус.')),
      },
    );
  };

  const toggleActive = (row: ManagerStatusDto) => {
    setRowError(row.id, undefined);
    patch.mutate(
      { statusId: row.id, patch: { active: !row.active } },
      { onError: (cause) => setRowError(row.id, describeStatusError(cause, 'Не удалось изменить статус.')) },
    );
  };

  if (statuses.isPending) {
    return (
      <div className="card" data-testid="statuses-skeleton">
        <div className="skeleton skeleton--line skeleton--wide" />
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--line" />
      </div>
    );
  }

  if (statuses.isError) {
    return (
      <div className="card">
        <ErrorState
          message={statuses.error instanceof Error ? statuses.error.message : 'Не удалось загрузить статусы.'}
          onRetry={() => void statuses.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="card card--flush" data-testid="statuses-panel">
      <div className="statuses-toolbar">
        <form
          className="statuses-toolbar__form"
          data-testid="status-create-form"
          onSubmit={(event) => {
            event.preventDefault();
            submitCreate();
          }}
        >
          <Input
            data-testid="status-create-input"
            aria-label="Название статуса"
            placeholder="Новый статус — например, «Перезвонить»"
            value={draft}
            aria-invalid={draftError !== null}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
              setDraftError(null);
              setToolbarError(null);
            }}
          />
          <Button type="submit" variant="primary" size="sm" loading={create.isPending} data-testid="status-create-submit">
            Добавить
          </Button>
          <label className="statuses-toolbar__toggle">
            <input
              type="checkbox"
              data-testid="status-include-inactive"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.currentTarget.checked)}
            />
            Показать деактивированные
          </label>
        </form>
        {draftError ?? toolbarError ? (
          <p className="user-role-error" role="alert" data-testid="status-create-error">
            {draftError ?? toolbarError}
          </p>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="Статусов пока нет"
          hint="Добавьте первый статус — он появится в фильтре реестра и в карточке заявления."
        />
      ) : (
        <TableWrap>
          <table className="data" data-testid="statuses-table">
            <thead>
              <tr>
                <th scope="col">Статус</th>
                <th scope="col" className="num">
                  Порядок
                </th>
                <th scope="col">Состояние</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowError = rowErrors[row.id];
                const editing = editingId === row.id;
                const saving = patch.isPending && patch.variables?.statusId === row.id;
                return (
                  <tr
                    key={row.id}
                    className={row.active ? 'status-row' : 'status-row status-row--inactive'}
                    data-testid="status-row"
                    data-status-id={row.id}
                    data-active={String(row.active)}
                  >
                    <td data-testid={`status-label-${row.id}`}>
                      {editing ? (
                        <span className="status-row__edit">
                          <Input
                            data-testid={`status-edit-input-${row.id}`}
                            aria-label="Новое название статуса"
                            value={editDraft}
                            aria-invalid={statusLabelError(editDraft) !== undefined}
                            onChange={(event) => setEditDraft(event.currentTarget.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                submitRename(row);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="primary"
                            data-testid={`status-edit-save-${row.id}`}
                            loading={saving}
                            onClick={() => submitRename(row)}
                          >
                            Сохранить
                          </Button>
                          <Button size="sm" data-testid={`status-edit-cancel-${row.id}`} onClick={() => setEditingId(null)}>
                            Отмена
                          </Button>
                        </span>
                      ) : (
                        row.label
                      )}
                      {rowError ? (
                        <span className="user-role-error" role="alert" data-testid={`status-error-${row.id}`}>
                          {rowError}
                        </span>
                      ) : null}
                    </td>
                    <td className="num" data-testid={`status-order-${row.id}`}>
                      {row.sortOrder}
                    </td>
                    <td data-testid={`status-active-${row.id}`}>
                      {row.active ? (
                        <Badge tone="ok">активен</Badge>
                      ) : (
                        <Badge tone="neutral" title="Новым заявлениям назначить нельзя — история сохраняется">
                          {STATUS_INACTIVE_LABEL}
                        </Badge>
                      )}
                    </td>
                    <td className="row-actions">
                      <Button
                        size="sm"
                        data-testid={`status-rename-${row.id}`}
                        onClick={() => {
                          setEditingId(row.id);
                          setEditDraft(row.label);
                        }}
                      >
                        Переименовать
                      </Button>
                      <Button
                        size="sm"
                        variant={row.active ? 'secondary' : 'primary'}
                        data-testid={`status-toggle-${row.id}`}
                        loading={saving}
                        onClick={() => toggleActive(row)}
                      >
                        {statusToggleLabel(row)}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      <p className="settings-note" data-testid="statuses-note">
        {STATUS_NO_DELETE_HINT} История назначений сохраняется: у уже назначенных заявлений ничего не меняется, а
        деактивированный статус нельзя назначить новым. Переименование и деактивация пишутся в журнал аудита.
        {patch.isPending ? <Spinner label="Сохраняем" /> : null}
      </p>
    </div>
  );
}

/** The api's refusals, in the words the operator acts on; the rest keep their message. */
function describeStatusError(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.is('CONFLICT', 409)) return DUPLICATE_HINT;
  return error instanceof Error ? error.message : fallback;
}
